import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileExcel, faFilePdf, faEye } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useParams } from 'react-router-dom';
import { useReports } from '../hooks';
import { Routes } from '../routes';
import Breadcrumbs from '../components/Breadcrumbs';

const ReportPreview = () => {
  const history = useHistory();
  const { eventId } = useParams();
  const reports = useReports();
  const [eventData, setEventData] = useState(null);
  const [reportLoaded, setReportLoaded] = useState(false);

  useEffect(() => {
    if (eventId && !reportLoaded) {
      // Obtener datos del evento desde localStorage o state
      const storedEventData = localStorage.getItem('selectedEventForReport');
      if (storedEventData) {
        const event = JSON.parse(storedEventData);
        setEventData(event);
        
        // Cargar el reporte HTML solo una vez
        const eventTitle = event.summary || event.title || event.nombre || 'Evento';
        reports.viewHTMLReport(eventId, eventTitle);
        setReportLoaded(true);
      }
    }
  }, [eventId, reportLoaded, reports.viewHTMLReport]);

  // Resetear el flag cuando cambia el eventId
  useEffect(() => {
    setReportLoaded(false);
  }, [eventId]);

  const handleBackToReports = () => {
    // Limpiar localStorage
    localStorage.removeItem('selectedEventForReport');
    setReportLoaded(false);
    history.push(Routes.Reports.path);
  };

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);
  
  const handleDownloadReport = (format) => {
    setDownloadFormat(format);
    setShowDownloadModal(true);
  };
  
  const confirmDownload = (soloAsistentes = true) => {
    if (eventData) {
      const eventTitle = eventData.summary || eventData.title || eventData.nombre || 'Evento';
      
      if (soloAsistentes) {
        // Usar la función original para solo asistentes
        reports.downloadReport(eventId, downloadFormat, eventTitle);
      } else {
        // Usar URL personalizada para todos los invitados
        const url = `/eventos/${eventId}/reporte/descargar/${downloadFormat}/?solo_asistentes=false`;
        reports.downloadReportCustom(url, eventTitle, downloadFormat);
      }
    }
    
    setShowDownloadModal(false);
    setDownloadFormat(null);
  };

  if (!eventData) {
    return (
      <Container fluid className="px-4">
        <Row className="justify-content-center">
          <Col xs={12} className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Cargando datos del evento...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  const eventTitle = eventData.summary || eventData.title || eventData.nombre || 'Evento sin título';

  return (
    <Container fluid className="px-4">
      <Row className="justify-content-md-center">
        <Col xs={12}>
          <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
            <div className="d-block mb-4 mb-md-0">
              <Breadcrumbs />
              <h2 className="h4">
                <FontAwesomeIcon icon={faEye} className="me-2" />
                Vista Previa del Reporte
              </h2>
              <p className="mb-0">Evento: {eventTitle}</p>
            </div>
            <div className="d-flex gap-2">
              <Button 
                onClick={() => handleDownloadReport('xlsx')}
                disabled={reports.loadingReports}
                style={{
                  backgroundColor: '#217346',
                  borderColor: '#217346',
                  color: 'white'
                }}
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                Descargar Excel
              </Button>
              
              <Button 
                variant="danger" 
                onClick={() => handleDownloadReport('pdf')}
                disabled={reports.loadingReports}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                Descargar PDF
              </Button>
              
              <Button 
                variant="outline-secondary" 
                onClick={handleBackToReports}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Regresar
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Reporte de Asistencia - {eventTitle}</h5>
            </Card.Header>
            <Card.Body>
              {reports.loadingReports && (
                <div className="text-center py-5">
                  <Spinner animation="border" className="me-2" />
                  Generando reporte...
                </div>
              )}

              {reports.error && (
                <Alert variant="danger">
                  {reports.error}
                </Alert>
              )}

              {reports.htmlContent && !reports.loadingReports && (
                <div 
                  dangerouslySetInnerHTML={{ __html: reports.htmlContent }}
                  style={{ 
                    minHeight: '400px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                />
              )}

              {!reports.htmlContent && !reports.loadingReports && !reports.error && (
                <div className="text-center py-5">
                  <p className="text-muted">No se pudo cargar el contenido del reporte.</p>
                  <Button 
                    variant="outline-primary" 
                    onClick={() => {
                      const eventTitle = eventData.summary || eventData.title || eventData.nombre || 'Evento';
                      reports.viewHTMLReport(eventId, eventTitle);
                    }}
                  >
                    Reintentar
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Modal de Confirmación de Descarga */}
      <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Tipo de Reporte</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Qué tipo de reporte deseas descargar?</p>
          <div className="d-grid gap-2">
            <Button 
              variant="outline-primary" 
              onClick={() => confirmDownload(true)}
              className="text-start"
            >
              <strong>Solo Asistentes</strong>
              <br />
              <small className="text-muted">Incluye únicamente las personas que confirmaron asistencia</small>
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={() => confirmDownload(false)}
              className="text-start"
            >
              <strong>Todos los Invitados</strong>
              <br />
              <small className="text-muted">Incluye todos los invitados (asistieron o no)</small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDownloadModal(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ReportPreview;